import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react'
import { ApplyDiscountVoucher } from '@moonpig/web-shared-promotions'
import {
  GetInitialPropsReturn,
  PageContext,
  PageEnvironment,
  PageRedirectResponse,
} from '@moonpig/web-core-app'
import { router, Routes, useRouter } from '@moonpig/web-core-routing'
import {
  RecentlyViewedProvider,
  FavouritesProvider,
  BasketProvider,
  DispatchCutOffQuery,
  DispatchCutOffQueryVariables,
  DispatchCutOffGQL,
  createViewItemListGAEvent,
} from '@moonpig/web-shared-products'
import {
  createScreenViewEvent,
  trackGAEvent,
  useTrackGAEventOnce,
} from '@moonpig/web-core-analytics'
import {
  DepartmentsEnum,
  Region as GraphQLRegion,
  SortOrder,
} from '@moonpig/web-explore-types-graphql'
import { logger } from '@moonpig/web-core-monitoring'
import {
  getParentDepartment,
  getSearchFilterWithLabel,
  getSearchCriteria,
  parseUrlQuerySearchTerm,
} from '@moonpig/web-core-utils'
import { Region } from '@moonpig/web-core-types'
import { useApolloClient } from '@moonpig/web-core-graphql'
import { useFlags } from '@moonpig/web-explore-flags'
import { getBrowserCookies } from '@moonpig/web-core-browser-storage'
import { useExperimentString } from '@moonpig/web-core-eppo/browser'
import { createSearchGAEvent } from '../../analytics/createSearchGAEvent'
import { sanitize, sanitizeCollection, useScrollRestoration } from '../../utils'
import {
  GALLERY_PAGE_SIZE,
  notRudeFilter,
  SEARCH_PAGE_TYPE,
} from '../../constants'
import { SearchHead } from './SearchHead'
import {
  SearchFeatures,
  SearchFilter,
  ServiceOptions,
  createServices,
  loadFeatures,
  loadOptions,
} from '../../services'
import {
  LoadFiltersResponse,
  SearchRequestResponse,
} from '../../services/types/services'
import { getGAListDataType } from '../../utils/getGAListDataType'
import { SearchDetails, SelectedSuggestion } from '../../components/types'
import { DrawerProvider } from '../../contexts/drawer'
import { PageType } from '../../types'
import { SearchWrapper } from './SearchWrapper'
import { SearchStoreProvider } from '../../store/SearchStore'
import { SearchStoreState } from '../../store/types'
import {
  applySelectedFilters,
  buildInitialState,
  getPersistedFilters,
  getSelectedFilters,
  removePreAppliedFilters,
} from '../../store/helpers'
import { useNbaFilters } from '../../utils/nbaFilters'
import { getIsFilterApplied } from '../../utils/getIsFilterApplied'

// eslint-disable-next-line @typescript-eslint/ban-types
export type PageContent<TProps = {}> = FC<TProps> & {
  getInitialProps(
    context: PageContext<Routes['search']> & {
      pageType?: string
      dynamicSearchTerm?: string
      dynamicDepartment?: DepartmentsEnum[]
      isIndexed?: boolean
    },
  ): Promise<PageRedirectResponse | GetInitialPropsReturn<TProps>>
}

type SearchPageProps = {
  initialFiltersResponse: LoadFiltersResponse
  initialResponse: SearchRequestResponse
  page: number
  region: Region
  details: SearchDetails
  filters: SearchFilter[]
  experiments?: { [key: string]: string } | undefined
  query: Routes['search'] | Routes['pay-per-click']
  dispatchCutOffData?: DispatchCutOffQuery
  environment?: PageEnvironment
  isIndexed: boolean
  selectedSuggestion?: SelectedSuggestion
  groupCardProject?: string
  options: ServiceOptions
  features: SearchFeatures
  luxLabel: string
  pageType: string
  pageTitle: string
  headOverride?: ReactElement
  redirectedDepartment?: DepartmentsEnum
  useClientSideRouting?: boolean
}

export const SearchPage: PageContent<SearchPageProps> = ({
  page: initialPage,
  initialFiltersResponse,
  initialResponse,
  region,
  details,
  filters: initialFilters,
  experiments,
  query,
  dispatchCutOffData,
  selectedSuggestion,
  options,
  features,
  headOverride,
  luxLabel,
  pageType,
  pageTitle,
  redirectedDepartment,
  useClientSideRouting = true,
}) => {
  const { department, searchTerm } = details
  const client = useApolloClient()
  const head = headOverride || <SearchHead region={region} />
  const flags = useFlags()

  const useNamedEntityRecognition =
    useExperimentString('search-use-named-entity-recognition', {
      fallback: 'control',
    })?.toLowerCase() === 'enabled'

  const useSmartDepartmentRedirection =
    useExperimentString('search-use-smart-department-redirection', {
      fallback: 'control',
    })?.toLowerCase() === 'enabled'

  const services = useMemo(() => {
    const context = {
      region,
      options,
      features,
      client,
      cookies: getBrowserCookies(),
    }

    return createServices(context)
  }, [client, features, options, region])

  const routerHook = useRouter()

  useScrollRestoration()

  const { trackGAEventOnce } = useTrackGAEventOnce()

  const filtersQuery = (clientFilters: SearchFilter[]) =>
    services.loadFilters({
      facets: clientFilters,
      departments: department,
      promotionId: details.promotionId,
      searchTerm: details.correctedSearchTerm || searchTerm,
    })

  const preAppliedFilters = selectedSuggestion?.facetKey
    ? [
        {
          group: selectedSuggestion.group,
          key: selectedSuggestion.facetKey,
          suggestion: true,
        },
      ]
    : []

  const persistedFilters = getPersistedFilters()

  const hasNbaFilters = useNbaFilters(initialFiltersResponse.filters).length > 0
  const hasRudeFilterEnabled = getIsFilterApplied(
    initialFiltersResponse.filters,
    'not-rude',
    false,
  )

  const createInitialState = () =>
    buildInitialState({
      pageTitle,
      pageType: pageType as PageType,
      router: routerHook,
      department,
      initialResponse,
      selectedSuggestion,
      redirectedDepartment,
      redirectionSource: useSmartDepartmentRedirection
        ? 'smart_redirection'
        : 'no_results',
      searchTerm,
      correctedSearchTerm: details.correctedSearchTerm,
      promotionId: details.promotionId,
      initialFiltersResponse,
      initialFilters,
      persistedFilters,
      preAppliedFilters,
      allFilters: applySelectedFilters(
        removePreAppliedFilters(
          initialFiltersResponse.filters,
          preAppliedFilters,
        ),
        [...persistedFilters, ...initialFilters.map(f => f.key)],
      ),
      selectedFilters: getSelectedFilters({
        initialFilters,
        persistedFilters,
        allFilters: initialFiltersResponse.filters,
        useAutoAppliedFilters: useNamedEntityRecognition,
      }),
      hasNbaFilters,
      hasRudeFilterEnabled,
      filtersQuery,
      services,
    })

  const [initialState, setInitialState] = useState<Partial<SearchStoreState>>(
    createInitialState(),
  )

  useEffect(() => {
    if (
      initialState.searchContext?.searchTerm !== searchTerm ||
      initialState.searchContext?.departments[0] !== department[0] ||
      initialState.searchContext?.selectedSuggestion?.facetKey !==
        selectedSuggestion?.facetKey
    ) {
      setInitialState({
        ...initialState,
        ...createInitialState(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedSuggestion, details.promotionId, department[0]])

  useEffect(() => {
    trackGAEventOnce(
      createScreenViewEvent({
        screenClass: 'search results',
        screenName: `search results | ${
          selectedSuggestion?.label ?? details.searchTerm
        }`.toLowerCase(),
        screenData: {
          renderType: 'client',
          documentReferrer: document.referrer,
          documentTitle: document.title.toLowerCase(),
          documentUrl: document.location.href,
        },
      }),
    )

    trackGAEventOnce(
      createSearchGAEvent({
        correctedSearchTerm: details.correctedSearchTerm || '',
        department,
        filters: initialFilters,
        filteringEvent: initialState.filteringEvent,
        numberOfResults: initialResponse.totalCount,
        searchTerm: details.searchTerm,
        sort:
          initialState.sortValue ||
          /* istanbul ignore next */ SortOrder.POPULARITY,
        selectedSuggestion,
        showNbas: hasNbaFilters,
        showRudeProducts: hasRudeFilterEnabled,
        redirectedDepartment,
        redirectionSource: useSmartDepartmentRedirection
          ? 'smart_redirection'
          : 'no_results',
      }),
    )

    if (initialState.results) {
      const search_data = {
        index: '',
        input_type: 'keyword',
        number_of_results: initialState.results.totalCount,
        product_department: department[0],
        search_term: searchTerm,
        page_title: pageTitle,
      }
      const label = `${pageType} | ${department[0]} | ${searchTerm} | ${pageTitle}`
      const event = createViewItemListGAEvent({
        products: initialState.results.products,
        label,
        search_data,
      })
      trackGAEvent(event)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState])

  return (
    <RecentlyViewedProvider>
      <BasketProvider>
        <FavouritesProvider
          location="search"
          enableToastNotification={flags['enable-fav-toast-notification']}
        >
          <DrawerProvider>
            {head}
            <ApplyDiscountVoucher uqd={query.uqd} />
            <SearchStoreProvider initialState={initialState}>
              <SearchWrapper
                luxLabel={luxLabel}
                useClientSideRouting={useClientSideRouting}
                experiments={experiments}
                dispatchCutOffData={dispatchCutOffData}
                initialPage={initialPage}
                pageTitle={pageTitle}
                groupCardProject={query.groupCardProject}
                pageType={pageType}
                department={department}
                searchTerm={searchTerm}
                selectedSuggestion={selectedSuggestion}
              />
            </SearchStoreProvider>
          </DrawerProvider>
        </FavouritesProvider>
      </BasketProvider>
    </RecentlyViewedProvider>
  )
}

SearchPage.getInitialProps = async ({
  region,
  query: typedQuery,
  path,
  graphQL,
  flags,
  getAllExperimentVariants,
  environment,
  getCookie,
  pageType = SEARCH_PAGE_TYPE,
  platform,
  dynamicSearchTerm,
  dynamicDepartment,
  isIndexed,
  getExperiment,
}) => {
  const redirectedDepartment = typedQuery.redirectedDepartment as
    | DepartmentsEnum
    | undefined

  const experiments = await getAllExperimentVariants()

  const {
    department: queryDepartment,
    filters: complexFilters,
    promotionId,
  } = getSearchCriteria(typedQuery)

  const department = dynamicDepartment || queryDepartment
  const parentDepartment = getParentDepartment(department[0])

  const options = loadOptions({
    query: typedQuery,
    pageType,
    platform,
    department: parentDepartment,
  })

  const features = loadFeatures({
    experiments,
    region,
    flags,
  })

  const cookies = {
    mnpg_exclude_rude_products: getCookie('mnpg_exclude_rude_products'),
  }

  const context = {
    region,
    options,
    features,
    client: graphQL,
    cookies,
  }

  const services = createServices(context)

  const query = typedQuery as unknown as PageContext['query']

  const selectedSuggestion = getSearchFilterWithLabel(
    query.suggestion as string,
  )

  const excludeRude =
    cookies.mnpg_exclude_rude_products === 'true' &&
    parentDepartment === ('ALL_CARDS' as DepartmentsEnum)

  const combinedFiltersWithoutSuggestion = [
    ...complexFilters,
    ...(excludeRude ? [notRudeFilter] : []),
  ].map(f => ({
    key: f.facetKey,
    group: f.group,
    userApplied: f.userApplied,
  }))

  const combinedFiltersWithSuggestion: SearchFilter[] = [
    ...combinedFiltersWithoutSuggestion,
    ...(selectedSuggestion
      ? [
          {
            key: selectedSuggestion.facetKey,
            group: selectedSuggestion.group,
            userApplied: true,
            suggestion: true,
          },
        ]
      : []),
  ]

  const dispatchCutOffPromise = graphQL.query<
    DispatchCutOffQuery,
    DispatchCutOffQueryVariables
  >({
    query: DispatchCutOffGQL,
    variables: {
      filter: [department[0], parentDepartment],
      region: region as GraphQLRegion,
      preview: undefined,
    },
  })

  const searchTerm =
    dynamicSearchTerm || parseUrlQuerySearchTerm(typedQuery.q) || ''
  const isLegacySearchUrl =
    typedQuery.f || typedQuery.product_categories || typedQuery.fq

  if (isLegacySearchUrl) {
    logger.info(`Legacy search URL transform - query: ${JSON.stringify(query)}`)
  }

  const page = (Number(query.offset || 0) || 0) / GALLERY_PAGE_SIZE + 1

  const isNERExperiment =
    getExperiment('search-use-named-entity-recognition', {
      fallback: 'disabled',
    })?.toLowerCase() === 'enabled'

  const isSmartDepartmentRedirectionExperiment =
    getExperiment('search-use-smart-department-redirection', {
      fallback: 'disabled',
    })?.toLowerCase() === 'enabled' && typedQuery.smartRedirection === true

  try {
    const isInitialQuery =
      isNERExperiment || isSmartDepartmentRedirectionExperiment

    const searchQuery = services.search({
      searchTerm,
      filters: combinedFiltersWithSuggestion,
      departments: department,
      page,
      sponsoredProducts: [],
      isInitialQuery,
    })

    const [{ data: dispatchCutOffData }, initialResponse] = await Promise.all([
      dispatchCutOffPromise,
      searchQuery,
    ])

    const { spellingCorrection, totalCount } = initialResponse

    const initialFiltersResponse = await services.loadFilters({
      facets: combinedFiltersWithSuggestion,
      departments: department,
      promotionId,
      searchTerm: spellingCorrection?.correctedSearchTerm || searchTerm,
      isInitialQuery,
    })

    if (
      isSmartDepartmentRedirectionExperiment &&
      initialResponse.redirectedDepartment
    ) {
      const { as } = router.build('search', {
        region,
        ...query,
        d: initialResponse.redirectedDepartment,
        smartRedirection: undefined,
      })

      return {
        pageRedirect: {
          statusCode: 301,
          location: `${as}&redirectedDepartment=${department[0]}`,
        },
      }
    }

    if (initialResponse.departments.length > 0) {
      const departmentToRedirect = initialResponse.departments.reduce(
        (maxObject, currentObject) => {
          // istanbul ignore next
          return currentObject.count > maxObject.count
            ? currentObject
            : maxObject
        },
        initialResponse.departments[0],
      )

      if (initialResponse.totalCount === 0 && departmentToRedirect?.count > 0) {
        const { as } = router.build('search', {
          region,
          ...query,
          d: departmentToRedirect.department,
        })

        return {
          pageRedirect: {
            statusCode: 301,
            location: `${as}&redirectedDepartment=${department[0]}`,
          },
        }
      }
    }

    return {
      initialFiltersResponse,
      initialResponse,
      page,
      region: region as Region,
      details: {
        region,
        department,
        url: path,
        searchTerm,
        promotionId,
        correctedSearchTerm: spellingCorrection?.correctedSearchTerm,
      },
      experiments,
      filters: combinedFiltersWithoutSuggestion,
      query: typedQuery,
      pageOptions: {
        layout: {
          nonStickyHeader: true,
        },
        tracking: {
          type: getGAListDataType(pageType),
          disableDefaultPageTracking: true,
          sortby: 'popularity - default',
          listData: {
            results: totalCount,
            type: getGAListDataType(pageType),
            name: `${pageType} - ${sanitize(searchTerm)}`,
            terms: sanitize(searchTerm) || '',
            filters: {
              facets: sanitizeCollection(combinedFiltersWithSuggestion),
              promotionId: sanitize(promotionId),
            },
          },
        },
      },
      dispatchCutOffData,
      environment,
      selectedSuggestion,
      groupCardProject: typedQuery.groupCardProject,
      options,
      features,
      luxLabel: 'Search Results Page',
      pageType,
      pageTitle: 'Search Results',
      isIndexed: isIndexed || false,
      redirectedDepartment,
    }
  } catch (error) {
    return {
      pageError: {
        statusCode: 500,
      },
    }
  }
}
